import { Box, Text } from "@chakra-ui/react";
import { Global } from "@emotion/react";
// Use the MarketCast ui-kit components so the dropdown matches the design
// system (light popover, gray option text, blue selection highlight + check).
import { DropDown, TextArea2, theme as marketcastTheme } from "@marketcast/ui-kit";
import { DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE } from "@/constants";

interface SelectFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void | undefined;
    options?: Array<{ id: string; name: string }>;
}

const SELECTED_CHECK_COLOR = marketcastTheme.colors.marketcast.primaryCta["1"];

/**
 * Draw a blue check on the right edge of the currently selected option.
 * The ui-kit's built-in indicator renders no visible glyph, so we add our own
 * via a CSS ::after. The option row's label fills the width, so the check is
 * absolutely positioned at the right edge (and the row gets right padding so
 * text never sits under it). Scoped to the selected row via both react-select's
 * class and aria-selected so it works regardless of platform quirks.
 */
const DropdownOptionStyles = () => (
    <Global
        styles={{
            '[role="option"]': {
                position: "relative",
                overflow: "visible !important",
                paddingRight: "36px !important",
            },
            '[role="option"][aria-selected="true"]::after, [role="option"][class*="is-selected"]::after':
                {
                    content: '"\\2713"', // ✓
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: SELECTED_CHECK_COLOR,
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "1",
                },
        }}
    />
);

export const SelectField: React.FC<SelectFieldProps> = ({
    label,
    value,
    onChange,
    options
}) => {
    const isDescription = label?.toLowerCase() === "description";

    // Map the app's { id, name } option shape to the ui-kit { value, label } shape.
    const dropdownOptions = (options ?? []).map((option) => ({
        value: option.id,
        label: option.name,
    }));

    return (
        <Box width="100%">
            {!isDescription && (
                <Text
                    mb={2}
                    fontSize={DEFAULT_FONT_SIZE}
                    fontFamily={DEFAULT_FONT_FAMILY}
                    fontWeight="600"
                    color="marketcast.text.primary"
                >
                    {label}
                </Text>
            )}

            {isDescription ? (
                <Box
                    width="100%"
                    sx={{
                        "& > div, & textarea": {
                            width: "100%",
                        },
                    }}
                >
                    <TextArea2
                        label={label}
                        placeholder="Add an optional description for this wave"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        minRows={3}
                        showClear
                        onClear={() => onChange("")}
                        isResizable={false}
                    />
                </Box>
            ) : (
                <Box width="100%" sx={{ "& > div": { width: "100%" } }}>
                    <DropdownOptionStyles />
                    <DropDown
                        options={dropdownOptions}
                        value={value}
                        onChange={(next) => onChange(next ?? "")}
                        placeholder={`Select ${label}`}
                    />
                </Box>
            )}
        </Box>
    );
};

export default SelectField;
